import { Badge, Stat } from "../../ds";
import type { DeviceDetail, SystemInfo } from "../../lib/api";
import { fmtDateTime, fmtKb, fmtUptime, relTime, usedPct } from "../../lib/format";
import { KeyValues } from "../KeyValues";
import { SectionCard } from "../SectionCard";
import { useSection } from "../../lib/hooks/useSection";

// Sektion „Übersicht“ → action `system_info`.
export function OverviewSection({ device }: { device: DeviceDetail }) {
  const section = useSection<SystemInfo>(device, "overview");

  return (
    <SectionCard
      title="Übersicht"
      description="Hostname, Firmware, Uptime, Auslastung"
      section={section}
      deviceOnline={device.online}
    >
      {(info) => {
        const ram = usedPct(info.memory?.totalKb, info.memory?.freeKb);
        const flash = usedPct(info.flash?.totalKb, info.flash?.freeKb);
        return (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Uptime" value={fmtUptime(info.uptimeSecs)} iconName="timer" />
              <Stat
                label="Load (1 Min)"
                value={info.load?.[0] !== undefined ? info.load[0].toFixed(2) : "—"}
                iconName="activity"
              />
              <Stat
                label="RAM belegt"
                value={ram ?? "—"}
                unit={ram !== null ? "%" : undefined}
                iconName="chip"
              />
              <Stat
                label="Flash belegt"
                value={flash ?? "—"}
                unit={flash !== null ? "%" : undefined}
                iconName="box"
              />
            </div>

            <KeyValues
              items={[
                [
                  "Status",
                  <Badge variant={device.online ? "success" : "neutral"} dot>
                    {device.online ? "online" : "offline"}
                  </Badge>,
                ],
                [
                  "Zuletzt gesehen",
                  `${fmtDateTime(device.lastSeen)} (${relTime(device.lastSeen)})`,
                ],
                ["Hostname", info.hostname ?? device.hostname ?? "—"],
                ["Modell", info.model ?? device.model ?? "—"],
                ["Firmware", info.firmware ?? device.firmwareVersion ?? "—"],
                // Der Bau-Stempel aus /etc/eulektro_release: welcher unserer
                // Builds das ist. Zwei Builds derselben OpenWrt-Version
                // unterscheiden sich sonst in nichts Sichtbarem.
                [
                  "Image-Build",
                  <span className="font-mono text-xs">{device.imageBuild || "—"}</span>,
                ],
                ["Kernel", info.kernel ?? "—"],
                ["Agent-Version", info.agentVersion ?? device.agentVersion ?? "—"],
                ["Device-ID", <span className="font-mono text-xs">{device.id}</span>],
                ["WG-IP (Uplink)", <span className="font-mono text-xs">{device.wgIp ?? "—"}</span>],
                ["Enrollt am", fmtDateTime(device.enrolledAt)],
                ["Load (1/5/15)", info.load ? info.load.map((l) => l.toFixed(2)).join(" / ") : "—"],
                [
                  "RAM",
                  info.memory
                    ? `${fmtKb(info.memory.totalKb - info.memory.freeKb)} von ${fmtKb(info.memory.totalKb)}`
                    : "—",
                ],
                [
                  "Flash",
                  info.flash
                    ? `${fmtKb(info.flash.totalKb - info.flash.freeKb)} von ${fmtKb(info.flash.totalKb)}`
                    : "—",
                ],
              ]}
            />
          </div>
        );
      }}
    </SectionCard>
  );
}
